import { useEffect, useState } from 'react'

/**
 * Debounce function to limit how often a function is called
 */
const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return (...args: Parameters<T>): void => {
    if (timeout) {
      clearTimeout(timeout)
    }

    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

/**
 * Hook that returns true if the screen width is less than 768px
 * @param debounceTime Time to debounce the resize event in milliseconds
 * @return Boolean indicating if screen is mobile width
 */
const useIsMobile = (debounceTime = 250): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(false)

  useEffect(() => {
    // Check if window is defined (browser environment)
    if (typeof window === 'undefined') {
      return
    }

    // Function to check if the window width is less than 768px
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    // Set initial value
    checkIsMobile()

    // Create debounced resize handler
    const debouncedHandleResize = debounce(checkIsMobile, debounceTime)

    // Add event listener
    window.addEventListener('resize', debouncedHandleResize)

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  }, [debounceTime])

  return isMobile
}

export default useIsMobile
